import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { 
  DollarSign, 
  Edit, 
  Plus, 
  Trash2, 
  Save, 
  AlertCircle, 
  TrendingUp, 
  Settings,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';
import { FeeManagementService, FeeCategory, Fee } from '@/lib/fee-management';
import { useAuth } from '@/providers/AuthProvider';
import { formatCurrency } from '@/lib/utils';

interface FeeEditDialogProps {
  fee: Fee | null;
  categoryId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (categoryId: string, fee: Fee) => void;
}

function FeeEditDialog({ fee, categoryId, isOpen, onClose, onSave }: FeeEditDialogProps) {
  const [formData, setFormData] = useState<Partial<Fee>>({
    name: '',
    description: '',
    type: 'fixed',
    value: 0,
    currency: 'USD',
    isActive: false,
    applicableFeatures: []
  });
  const [featuresInput, setFeaturesInput] = useState('');

  useEffect(() => {
    if (fee) {
      setFormData(fee);
      setFeaturesInput(fee.applicableFeatures.join(', '));
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'fixed',
        value: 0,
        currency: 'USD',
        isActive: false,
        applicableFeatures: []
      });
      setFeaturesInput('');
    }
  }, [fee, isOpen]);

  const handleSave = () => {
    if (!formData.name || !formData.description || formData.value === undefined) {
      return;
    }

    const feeData: Fee = {
      id: fee?.id || `fee_${Date.now()}`,
      name: formData.name,
      description: formData.description,
      type: formData.type as Fee['type'],
      value: formData.value,
      currency: formData.currency || 'USD',
      isActive: formData.isActive || false,
      applicableFeatures: featuresInput.split(',').map(f => f.trim()).filter(f => f),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    };

    onSave(categoryId, feeData);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{fee ? 'Edit Fee' : 'Add New Fee'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Fee Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter fee name"
              />
            </div>
            <div>
              <Label htmlFor="type">Fee Type</Label>
              <Select value={formData.type} onValueChange={(value: string) => setFormData({ ...formData, type: value as Fee['type'] })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select fee type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="tiered">Tiered</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter fee description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="value">
                Fee Value {formData.type === 'percentage' ? '(%)' : ''}
              </Label>
              <Input
                id="value"
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) })}
                placeholder="Enter fee value"
              />
            </div>
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Select value={formData.currency} onValueChange={(value: string) => setFormData({ ...formData, currency: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="BTC">BTC</SelectItem>
                  <SelectItem value="ETH">ETH</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="features">Applicable Features (comma-separated)</Label>
            <Textarea
              id="features"
              value={featuresInput}
              onChange={(e) => setFeaturesInput(e.target.value)}
              placeholder="e.g., product_sale, digital_product_sale, marketplace_transaction"
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="active"
              checked={formData.isActive}
              onCheckedChange={(checked: boolean) => setFormData({ ...formData, isActive: checked })}
            />
            <Label htmlFor="active">Active</Label>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Save className="w-4 h-4 mr-2" />
              Save Fee
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FeeManagementDashboard() {
  const { user, userData } = useAuth();
  const { toast } = useToast();
  const [categories, setCategories] = useState<FeeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingFee, setEditingFee] = useState<{ fee: Fee | null; categoryId: string } | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const isSuperAdmin = userData?.role === 'superadmin' && userData?.email === 'milan.krnjajic@gmail.com';

  const auditContext = {
    actorId: user?.uid,
    actorEmail: user?.email || userData?.email,
    actorName: user?.displayName || userData?.displayName || userData?.email,
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadFeeCategories();
    }
  }, [isSuperAdmin]);

  const loadFeeCategories = async () => {
    try {
      setLoading(true);
      
      // Check if fee structure exists, if not initialize it
      let feeCategories = await FeeManagementService.getFeeCategories();
      
      if (feeCategories.length === 0) {
        await FeeManagementService.initializeFeeStructure();
        feeCategories = await FeeManagementService.getFeeCategories();
      }

      setCategories(feeCategories);
    } catch (error) {
      console.error('Error loading fee categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load fee categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFee = async (categoryId: string, fee: Fee) => {
    try {
      const existingFee = categories
        .find(c => c.id === categoryId)
        ?.fees.find(f => f.id === fee.id);

      const normalizedFee: Fee = {
        ...fee,
        lastUpdated: new Date().toISOString(),
        updatedBy: auditContext.actorEmail || auditContext.actorId || 'admin',
      };

      if (existingFee) {
        await FeeManagementService.updateFee(categoryId, fee.id, normalizedFee, auditContext);
      } else {
        await FeeManagementService.addFee(categoryId, normalizedFee, auditContext);
      }

      await loadFeeCategories();
      toast({
        title: 'Success',
        description: `Fee ${existingFee ? 'updated' : 'added'} successfully`,
      });
    } catch (error) {
      console.error('Error saving fee:', error);
      toast({
        title: 'Error',
        description: 'Failed to save fee',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteFee = async (categoryId: string, feeId: string) => {
    if (!confirm('Are you sure you want to delete this fee?')) {
      return;
    }

    try {
      await FeeManagementService.deleteFee(categoryId, feeId, auditContext);
      await loadFeeCategories();
      toast({
        title: 'Success',
        description: 'Fee deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting fee:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete fee',
        variant: 'destructive',
      });
    }
  };

  const handleToggleFeeStatus = async (categoryId: string, feeId: string, isActive: boolean) => {
    try {
      await FeeManagementService.updateFee(
        categoryId,
        feeId,
        {
          isActive,
          updatedBy: auditContext.actorEmail || auditContext.actorId || 'admin',
        },
        auditContext
      );
      await loadFeeCategories();
      toast({
        title: 'Success',
        description: `Fee ${isActive ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (error) {
      console.error('Error toggling fee status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update fee status',
        variant: 'destructive',
      });
    }
  };

  const getTotalRevenue = () => {
    return categories.reduce((total, category) => {
      return total + category.fees.reduce((catTotal, fee) => {
        return catTotal + (fee.isActive ? fee.value : 0);
      }, 0);
    }, 0);
  };

  const getActiveFees = () => {
    return categories.reduce((total, category) => {
      return total + category.fees.filter(fee => fee.isActive).length;
    }, 0);
  };

  const filteredCategories = selectedCategory === 'all' 
    ? categories 
    : categories.filter(cat => cat.id === selectedCategory);

  if (!isSuperAdmin) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <Card className="p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600">Only super admins can access fee management.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading fee management...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <DollarSign className="w-8 h-8 text-green-600" />
            Fee Management
          </h1>
          <p className="text-gray-600">Configure fees for all InTouch platform features</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive">Show Inactive</Label>
          </div>
          <Button onClick={loadFeeCategories} size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Fee Categories</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Fees</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{getActiveFees()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Potential Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalRevenue())}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Feature Coverage</CardTitle>
            <Eye className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {categories.reduce((total, cat) => total + cat.fees.length, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(category => (
              <SelectItem key={category.id} value={category.id}>
                {category.icon} {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fee Categories */}
      <div className="space-y-6">
        {filteredCategories.map(category => (
          <Card key={category.id}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">{category.icon}</span>
                  {category.name}
                </CardTitle>
                <Dialog>
                  <DialogTrigger>
                    <Button 
                      size="sm" 
                      onClick={() => setEditingFee({ fee: null, categoryId: category.id })}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Fee
                    </Button>
                  </DialogTrigger>
                </Dialog>
              </div>
              <p className="text-gray-600">{category.description}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category.fees
                  .filter(fee => showInactive || fee.isActive)
                  .map(fee => (
                    <div
                      key={fee.id}
                      className={`p-4 border rounded-lg ${
                        fee.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{fee.name}</h3>
                            <Badge variant={fee.isActive ? 'default' : 'secondary'}>
                              {fee.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {fee.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{fee.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="font-medium">
                              Value: {fee.type === 'percentage' ? `${fee.value}%` : formatCurrency(fee.value)}
                            </span>
                            <span className="text-gray-500">
                              Features: {fee.applicableFeatures.length}
                            </span>
                            <span className="text-gray-500">
                              Updated: {new Date(fee.lastUpdated).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleFeeStatus(category.id, fee.id, !fee.isActive)}
                          >
                            {fee.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingFee({ fee, categoryId: category.id })}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteFee(category.id, fee.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                
                {category.fees.filter(fee => showInactive || fee.isActive).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No {showInactive ? '' : 'active'} fees in this category
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Fee Edit Dialog */}
      {editingFee && (
        <FeeEditDialog
          fee={editingFee.fee}
          categoryId={editingFee.categoryId}
          isOpen={!!editingFee}
          onClose={() => setEditingFee(null)}
          onSave={handleSaveFee}
        />
      )}
    </div>
  );
}